// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import ChevronUp from "@store/Library/Components/Toolkit/Icons/ChevronUp";
import useAccordionState from "@store/Framework/Common/Hooks/useAccordionState";
import NavigationDrawerLink, {
    NavigationDrawerLinkPresentationProps,
    NavigationDrawerLinkProps,
} from "@store/Library/Components/NavigationDrawerLink";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, PropsWithChildren, useMemo } from "react";
import { css } from "styled-components";

export interface NavigationDrawerAccordionPresentationProps
    extends ExpandStyles<NavigationDrawerAccordionStyles>,
        Pick<NavigationDrawerLinkPresentationProps, "iconSrc"> {}

export interface NavigationDrawerAccordionProps
    extends NavigationDrawerAccordionPresentationProps,
        Pick<NavigationDrawerLinkProps, "subText" | "text"> {
    /** Controls the initial expanded state of the panel. */
    defaultExpanded?: boolean;
}

export interface NavigationDrawerAccordionStyles {
    navigationDrawerLink?: NavigationDrawerLinkPresentationProps;
    panel?: InjectableCss;
    wrapper?: InjectableCss;
}

export const navigationDrawerAccordionStyles: NavigationDrawerAccordionStyles = {
    panel: {
        css: css`
            padding: ${pxToRem(8, 20)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
};

const NavigationDrawerAccordion: FC<PropsWithChildren<NavigationDrawerAccordionProps>> = ({
    children,
    defaultExpanded = false,
    extendedStyles,
    iconSrc,
    overridedStyles,
    subText,
    text,
}) => {
    const baseStyles = useExpandProps(navigationDrawerAccordionStyles, extendedStyles, overridedStyles);
    const { isExpanded, triggerId, panelId, toggle } = useAccordionState(defaultExpanded);

    const styles = useMemo(() => {
        if (!iconSrc) {
            return baseStyles;
        }

        return mergeProps<NavigationDrawerAccordionStyles>(baseStyles, {
            panel: {
                css: css`
                    padding-left: ${pxToRem(56)};
                `,
            },
        });
    }, [baseStyles, iconSrc]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <NavigationDrawerLink
                {...styles.navigationDrawerLink}
                aria-controls={panelId}
                aria-expanded={isExpanded}
                iconSrc={iconSrc}
                id={triggerId}
                isCurrent={isExpanded}
                onClick={toggle}
                subText={subText}
                text={text}
                trailingIconSrc={isExpanded ? ChevronUp : ChevronDown}
            />
            <StyledWrapper {...styles.panel} aria-labelledby={triggerId} hidden={!isExpanded} id={panelId}>
                {children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default NavigationDrawerAccordion;
